import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type LiveVerdict = "pass" | "flag";

export interface LiveTranscriptTurn {
  role: string;
  message: string;
  time_in_call_secs?: number;
}

export interface LiveScenarioEvent {
  /** Stable id within this stream — derived from scenarioId or a synthesized fallback */
  key: string;
  scenarioId: number | string | null;
  category: string;
  categoryLabel: string;
  severity: string | null;
  transcript: LiveTranscriptTurn[];
  verdict: LiveVerdict;
  flagReason: string | null;
  receivedAt: number;
}

export interface LiveAuditStreamState {
  /** Scenarios that finished, in arrival order (newest last) */
  scenarios: LiveScenarioEvent[];
  /** True once the audit emits its terminal test-completed event */
  completed: boolean;
  /** True if the audit emitted test-failed / audit-failed */
  failed: boolean;
  failureReason: string | null;
  overallScore: number | null;
  testsComplete: number;
  totalTests: number;
}

const initial: LiveAuditStreamState = {
  scenarios: [],
  completed: false,
  failed: false,
  failureReason: null,
  overallScore: null,
  testsComplete: 0,
  totalTests: 0,
};

/**
 * Subscribes to the `brand-tests` broadcast channel and surfaces the
 * per-scenario events for the given audit run. The Live Audit page consumes
 * `scenarios` as a queue, paces them into tiles, and transitions to a report
 * card once `completed` flips true.
 *
 * Returns immutable state — caller is responsible for any local queue
 * management or display pacing.
 */
export function useLiveAuditStream(testRunId: number | null) {
  const [state, setState] = useState<LiveAuditStreamState>(initial);
  const queryClient = useQueryClient();
  const testRunIdRef = useRef(testRunId);

  useEffect(() => {
    testRunIdRef.current = testRunId;
  }, [testRunId]);

  // Reset when a new run id arrives
  useEffect(() => {
    setState(initial);
  }, [testRunId]);

  useEffect(() => {
    if (!testRunId) return;

    let isMounted = true;

    // Remove any existing channel with this name before creating a new one.
    // supabase.channel() reuses instances by topic — if useBrandIntegrityNotifications
    // left a stale/closing channel behind, subscribing to it would immediately
    // trigger CLOSED and miss all broadcasts.
    const stale = supabase.getChannels().find((c) => c.topic === "realtime:brand-tests");
    if (stale) supabase.removeChannel(stale);

    const channel = supabase.channel("brand-tests");

    const matches = (incoming: unknown) => Number(incoming) === testRunId;

    channel
      .on("broadcast", { event: "scenario-completed" }, ({ payload }: { payload: any }) => {
        if (!isMounted || !matches(payload?.testRunId)) return;
        setState((s) => {
          const key = payload.scenarioId != null
            ? `scenario-${payload.scenarioId}`
            : `synthetic-${s.scenarios.length}-${Date.now()}`;
          // Dedupe in case the same event arrives twice on reconnect
          if (s.scenarios.some((sc) => sc.key === key)) return s;
          const next: LiveScenarioEvent = {
            key,
            scenarioId: payload.scenarioId ?? null,
            category: payload.category ?? "unknown",
            categoryLabel: payload.categoryLabel ?? "Test",
            severity: payload.severity ?? null,
            transcript: Array.isArray(payload.transcript) ? payload.transcript : [],
            verdict: payload.verdict === "flag" ? "flag" : "pass",
            flagReason: payload.flagReason ?? null,
            receivedAt: Date.now(),
          };
          return { ...s, scenarios: [...s.scenarios, next] };
        });
      })
      .on("broadcast", { event: "test-started" }, ({ payload }: { payload: any }) => {
        if (!isMounted || !matches(payload?.testRunId)) return;
        setState((s) => ({
          ...s,
          totalTests: typeof payload.totalTests === "number" ? payload.totalTests : s.totalTests,
        }));
      })
      .on("broadcast", { event: "test-progress" }, ({ payload }: { payload: any }) => {
        if (!isMounted || !matches(payload?.testRunId)) return;
        setState((s) => ({
          ...s,
          testsComplete: typeof payload.progress === "number" ? payload.progress : s.testsComplete,
          totalTests: typeof payload.total === "number" ? payload.total : s.totalTests,
        }));
      })
      .on("broadcast", { event: "test-completed" }, ({ payload }: { payload: any }) => {
        if (!isMounted || !matches(payload?.testRunId)) return;
        setState((s) => ({
          ...s,
          completed: true,
          overallScore: typeof payload.overallScore === "number" ? payload.overallScore : s.overallScore,
        }));
        queryClient.invalidateQueries({ queryKey: ["brand-test-runs-infinite"] });
        queryClient.invalidateQueries({ queryKey: ["brand-test-run-details", String(testRunId)] });
      })
      .on("broadcast", { event: "test-failed" }, ({ payload }: { payload: any }) => {
        if (!isMounted || !matches(payload?.testRunId)) return;
        setState((s) => ({ ...s, failed: true, failureReason: payload?.error ?? "Audit failed" }));
      })
      .on("broadcast", { event: "audit-failed" }, ({ payload }: { payload: any }) => {
        if (!isMounted || !matches(payload?.testRunId)) return;
        setState((s) => ({ ...s, failed: true, failureReason: payload?.error ?? "Audit failed" }));
      })
      .subscribe();

    return () => {
      isMounted = false;
    };
  }, [testRunId]);

  return state;
}
